import { getLogConsole } from "../services/log.service";
import { objectKeys } from "./object.tools";

const CSS_COLOR_KEYWORDS = ["transparent", "currentColor", "inherit"];

export const HEXA_WHITE = "#FFFFFF";

/**
 * List of css colors
 * All modern browsers support the following 140 color names.
 * 
 * All values MUST be a valid ColorHex string.
 * MAB: I did not found a way to type it easily, so there an unit test that checks the format of the values.
 */
export const CSS_COLORS = {
    aliceblue: "#f0f8ff",
    antiquewhite: "#faebd7",
    aqua: "#00ffff",
    aquamarine: "#7fffd4",
    azure: "#f0ffff",
    beige: "#f5f5dc",
    bisque: "#ffe4c4",
    black: "#000000",
    blanchedalmond: "#ffebcd",
    blue: "#0000ff",
    blueviolet: "#8a2be2",
    brown: "#a52a2a",
    burlywood: "#deb887",
    cadetblue: "#5f9ea0",
    chartreuse: "#7fff00",
    chocolate: "#d2691e",
    coral: "#ff7f50",
    cornflowerblue: "#6495ed",
    cornsilk: "#fff8dc",
    crimson: "#dc143c",
    cyan: "#00ffff",
    darkblue: "#00008b",
    darkcyan: "#008b8b",
    darkgoldenrod: "#b8860b",
    darkgray: "#a9a9a9",
    darkgreen: "#006400",
    darkkhaki: "#bdb76b",
    darkmagenta: "#8b008b",
    darkolivegreen: "#556b2f",
    darkorange: "#ff8c00",
    darkorchid: "#9932cc",
    darkred: "#8b0000",
    darksalmon: "#e9967a",
    darkseagreen: "#8fbc8f",
    darkslateblue: "#483d8b",
    darkslategray: "#2f4f4f",
    darkturquoise: "#00ced1",
    darkviolet: "#9400d3",
    deeppink: "#ff1493",
    deepskyblue: "#00bfff",
    dimgray: "#696969",
    dodgerblue: "#1e90ff",
    firebrick: "#b22222",
    floralwhite: "#fffaf0",
    forestgreen: "#228b22",
    fuchsia: "#ff00ff",
    gainsboro: "#dcdcdc",
    ghostwhite: "#f8f8ff",
    gold: "#ffd700",
    goldenrod: "#daa520",
    gray: "#808080",
    green: "#008000",
    greenyellow: "#adff2f",
    honeydew: "#f0fff0",
    hotpink: "#ff69b4",
    indianred: "#cd5c5c",
    indigo: "#4b0082",
    ivory: "#fffff0",
    khaki: "#f0e68c",
    lavender: "#e6e6fa",
    lavenderblush: "#fff0f5",
    lawngreen: "#7cfc00",
    lemonchiffon: "#fffacd",
    lightblue: "#add8e6",
    lightcoral: "#f08080",
    lightcyan: "#e0ffff",
    lightgoldenrodyellow: "#fafad2",
    lightgrey: "#d3d3d3",
    lightgreen: "#90ee90",
    lightpink: "#ffb6c1",
    lightsalmon: "#ffa07a",
    lightseagreen: "#20b2aa",
    lightskyblue: "#87cefa",
    lightslategray: "#778899",
    lightsteelblue: "#b0c4de",
    lightyellow: "#ffffe0",
    lime: "#00ff00",
    limegreen: "#32cd32",
    linen: "#faf0e6",
    magenta: "#ff00ff",
    maroon: "#800000",
    mediumaquamarine: "#66cdaa",
    mediumblue: "#0000cd",
    mediumorchid: "#ba55d3",
    mediumpurple: "#9370d8",
    mediumseagreen: "#3cb371",
    mediumslateblue: "#7b68ee",
    mediumspringgreen: "#00fa9a",
    mediumturquoise: "#48d1cc",
    mediumvioletred: "#c71585",
    midnightblue: "#191970",
    mintcream: "#f5fffa",
    mistyrose: "#ffe4e1",
    moccasin: "#ffe4b5",
    navajowhite: "#ffdead",
    navy: "#000080",
    oldlace: "#fdf5e6",
    olive: "#808000",
    olivedrab: "#6b8e23",
    orange: "#ffa500",
    orangered: "#ff4500",
    orchid: "#da70d6",
    palegoldenrod: "#eee8aa",
    palegreen: "#98fb98",
    paleturquoise: "#afeeee",
    palevioletred: "#d87093",
    papayawhip: "#ffefd5",
    peachpuff: "#ffdab9",
    peru: "#cd853f",
    pink: "#ffc0cb",
    plum: "#dda0dd",
    powderblue: "#b0e0e6",
    purple: "#800080",
    rebeccapurple: "#663399",
    red: "#ff0000",
    rosybrown: "#bc8f8f",
    royalblue: "#4169e1",
    saddlebrown: "#8b4513",
    salmon: "#fa8072",
    sandybrown: "#f4a460",
    seagreen: "#2e8b57",
    seashell: "#fff5ee",
    sienna: "#a0522d",
    silver: "#c0c0c0",
    skyblue: "#87ceeb",
    slateblue: "#6a5acd",
    slategray: "#708090",
    snow: "#fffafa",
    springgreen: "#00ff7f",
    steelblue: "#4682b4",
    tan: "#d2b48c",
    teal: "#008080",
    thistle: "#d8bfd8",
    tomato: "#ff6347",
    turquoise: "#40e0d0",
    violet: "#ee82ee",
    wheat: "#f5deb3",
    white: "#ffffff",
    whitesmoke: "#f5f5f5",
    yellow: "#ffff00",
    yellowgreen: "#9acd32"
};

export type ColorName = keyof typeof CSS_COLORS;
export type ColorHex = `#${string}`;
export type ColorRgb = `rgb(${number},${number},${number})` | `rgb(${number}, ${number}, ${number})`;
export type ColorRgba = `rgba(${number},${number},${number},${number})` | `rgba(${number}, ${number}, ${number}, ${number})`;
export type ColorRgbValue = { r: number, g: number, b: number };
export type ColorRgbaValue = ColorRgbValue & { a: number };
export type ColorNameOrHex = ColorName | ColorHex;
export type ColorNameOrRgb = ColorName | ColorRgb;

export type ColorType = ColorNameOrHex | ColorRgb | ColorRgba;

/**
 * Array containing 13 light HTML color names
 */
export const LIGHT_COLORS: ColorName[] = objectKeys(CSS_COLORS).filter(color => color.startsWith("light"));

/**
 * Array containing 10 'standard' HTML color names
 */
export const STANDARD_COLORS: ColorName[] = [
    "blue", "green", "red", "cyan", "yellow", "gray", "pink", "indigo", "magenta", "maroon"
];

/** Returns hexCode for a given colorName or undefined if not found */
export function colorNameToHex(colorName: ColorName): ColorHex | undefined {
    // MAB: see CSS_COLORS description, there is a test that checks the format of the values, so it is a safe cast here
    return CSS_COLORS[colorName] as ColorHex | undefined;
}

/**
 * is color valid hexa?
 */
export function isValidHexColor(color: string, withAlpha: boolean = false): color is ColorHex {
    const regexp = withAlpha ? new RegExp("^#([A-Fa-f0-9]{4}|[A-Fa-f0-9]{8})$") : new RegExp("^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$");
    return regexp.test(color);
}

/**
 * is color valid rgb?
 */
export function isValidRgbColor(color: string): color is ColorRgb {
    const regexp = new RegExp(/^rgb\((0|255|25[0-4]|2[0-4]\d|1\d\d|0?\d?\d),( ?)(0|255|25[0-4]|2[0-4]\d|1\d\d|0?\d?\d),( ?)(0|255|25[0-4]|2[0-4]\d|1\d\d|0?\d?\d)\)$/);
    return regexp.test(color);
}

/**
 * is color valid rgba?
 */
export function isValidRgbaColor(color: string): color is ColorRgba {
    const regexp = new RegExp(/^rgba\((0|255|25[0-4]|2[0-4]\d|1\d\d|0?\d?\d),( ?)(0|255|25[0-4]|2[0-4]\d|1\d\d|0?\d?\d),( ?)(0|255|25[0-4]|2[0-4]\d|1\d\d|0?\d?\d),( ?)(0|1|0?\.\d+)\)$/);
    return regexp.test(color);
}

/**
 * Cast string as rgbColor
 */
export function asColorRgb(color: string): ColorRgb {
    if (!isValidRgbColor(color)) {
        getLogConsole().warn(`Color '${color}' is not a valid rgb color.`);
    }
    return color as ColorRgb;
}

/**
 * Cast string as hexColor
 */
export function asColorHex(color: string): ColorHex {
    if (!isValidHexColor(color)) {
        getLogConsole().warn(`Color '${color}' is not a valid hex color.`);
    }
    return color as ColorHex;
}

/**
 * Get rgb string from object
 * Values will be fixed between 0 and 255
 */
export function buildRgbString(rgbValues: ColorRgbValue): ColorRgb {
    return asColorRgb(`rgb(${handleRgbValue(rgbValues.r)}, ${handleRgbValue(rgbValues.g)}, ${handleRgbValue(rgbValues.b)})`);
}

/**
 * Handle correct rgb value
 * Value will be fixed between 0 and 255
 */
export function handleRgbValue(rgbValue: number): number {
    if (rgbValue == null || rgbValue < 0) { return 0; }
    if (rgbValue > 255) { return 255; }
    return rgbValue;
}

/**
 * Converts an RGB color value (with components ranging from 0 to 255) to a HEX color code.
 * 
 * @param {ColorRgb} rgb - The RGB color value in the format "rgb(r, g, b)", where r, g, and b are integers from 0 to 255.
 * @returns {ColorHex | undefined} - The HEX color code as a string (e.g., "#RRGGBB"), or undefined if the input is invalid.
 */
export function rgbToHex(rgb: ColorRgb): ColorHex | undefined {
    if (rgb != null) {
        // Check that the rgb parameter of the function matches the expected rgb format (only integers)
        const rgbstr = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
        if (rgbstr !== null) {
            // Retrieve values as number type
            const [r, g, b] = rgbstr.slice(1, 4).map(Number);

            // Condition to verify that the range is correctly between 0 and 255
            if ([r, g, b].some(v => v < 0 || v > 255)) {
                return undefined;
            }

            const toHex = (value: number): string => {
                return value.toString(16).padStart(2, "0");
            };

            // Conversion to Hex
            const hexValue: ColorHex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
            return hexValue;
        }
    }

    return undefined;
}

/**
 * Converts an RGBA color value (with components ranging from 0 to 255) to a HEX color code.
 * 
 * @param {ColorRgba} rgba - The RGBA color value in the format "rgba(r, g, b, a)", where r, g, b and a are integers from 0 to 255.
 * @returns {ColorHex | undefined} - The HEX color code as a string (e.g., "#RRGGBBAA"), or undefined if the input is invalid.
 */
export function rgbaToHex(rgba: ColorRgba): ColorHex | undefined {
    if (rgba != null) {
        const rgbastr = rgba.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*(\d+(?:\.\d+)?)\)$/);
        if (rgbastr !== null) {
            // Retrieve values as number type
            const [r, g, b, a] = rgbastr.slice(1, 5).map(Number);

            // Condition to verify that the range is correctly between 0 and 255
            if ([r, g, b].some(v => v < 0 || v > 255)) {
                return undefined;
            }

            // Condition to verify that the range is correctly between 0 and 1
            if (a < 0 || a > 1) {
                return undefined;
            }

            const toHex255 = (value: number): string => {
                return value.toString(16).padStart(2, "0");
            };

            const toHexAlpha = (value: number): string => {
                const alpha255 = Math.round(value * 255);
                return alpha255.toString(16).padStart(2, "0");
            };

            // Conversion to Hex
            const hexValue: ColorHex = `#${toHex255(r)}${toHex255(g)}${toHex255(b)}${toHexAlpha(a)}`;
            return hexValue;
        }
    }

    return undefined;
}

/**
 * Converts an RGB color value (with components ranging from 0 to 1) to a HEX color code.
 * Note: This function is different from rgbToHex, which works with RGB values ranging from 0 to 255.
 * 
 * @param {ColorRgb} rgb - The RGB color value in the format "rgb(r, g, b)", where r, g, and b are floats from 0 to 1.
 * @returns {ColorHex | undefined} - The HEX color code as a string (e.g., "#RRGGBB"), or undefined if the input is invalid.
 */
export function rgb01ToHex(rgb: ColorRgb): ColorHex | undefined {
    if (rgb != null) {
        // Check that the rgb parameter of the function matches the expected rgb format (decimal numbers allowed)
        const rgbstr = rgb.match(/^rgb\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)\)$/);
        if (rgbstr !== null) {
            // Retrieve values as float type
            const [r, g, b] = rgbstr.slice(1, 4).map(parseFloat);

            // Condition to verify that the range is correctly between 0 and 1
            if ([r, g, b].some(v => v < 0 || v > 1)) {
                return undefined;
            }

            const toHex = (value: number): string => {
                return Math.round(value * 255).toString(16).padStart(2, "0");
            };

            // Conversion to Hex
            const hex: ColorHex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
            return hex;
        }
    }

    return undefined;
}

/** Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF") */
export function expandShortHexColor(hex: ColorHex): ColorHex {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const hexString = hex.replace(shorthandRegex, (m, r, g, b) => {
        return "#" + r + r + g + g + b + b;
    });

    return asColorHex(hexString);// Allowed cast, hex respect {"#"+string} of ColorHex type
}

/** 
 * hex2rgb from https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
 */
export function hexToRgb(hex: ColorHex): ColorRgbValue | null {
    hex = expandShortHexColor(hex);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

export function hexToRgba(hex: ColorHex): ColorRgbaValue | null {
    hex = expandShortHexColor(hex);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: parseFloat((parseInt(result[4], 16) / 255).toFixed(2))
    } : null;
}


/** darken or lighten an hexadecimal color.
 * @param color a 7 character string representing an hexadecimal color (eg. #0A8DB1)
 * @param value -1.0 will darken to black, 0.0 is identity, 1.0 is lighten to white
 * https://stackoverflow.com/questions/5560248/programmatically-lighten-or-darken-a-hex-color-or-rgb-and-blend-colors */
export function shadeHexColor(color: ColorHex, value: number): ColorHex {
    const f = parseInt(color.slice(1), 16), t = value < 0 ? 0 : 255, p = value < 0 ? value * -1 : value, R = f >> 16, G = f >> 8 & 0x00FF, B = f & 0x0000FF;
    const valueHex = "#" + (0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B)).toString(16).slice(1);
    return asColorHex(valueHex);// Allowed cast, valueHex respect {"#"+string} of ColorHex
}

/**
 * @param hexColor a 7 character string representing an hexadecimal color (eg. #0A8DB1)
 * @returns boolean true if the hexa color is considered light, false otherwise
 * see https://stackoverflow.com/questions/596216/formula-to-determine-perceived-brightness-of-rgb-color for explanation on algo
 */
export function isLightHexColor(hexColor: ColorHex): boolean {
    if (!isValidHexColor(hexColor)) { return true; }

    // Send this function a decimal sRGB gamma encoded color value
    // between 0.0 and 1.0, and it returns a linearized value.
    const sRGBtoLin = (colorChannel: number): number => {
        if (colorChannel <= 0.04045) {
            return colorChannel / 12.92;
        } else {
            return Math.pow((colorChannel + 0.055) / 1.055, 2.4);
        }
    };

    const hexColorWithoutHash = hexColor.substring(1);// strip #
    const rgb = parseInt(hexColorWithoutHash, 16);// convert rrggbb to decimal
    const r = (rgb >> 16 & 0xff) / 255.0;  // extract red
    const g = (rgb >> 8 & 0xff) / 255.0;  // extract green
    const b = (rgb >> 0 & 0xff) / 255.0;  // extract blue

    const luma = 0.2126 * sRGBtoLin(r) + 0.7152 * sRGBtoLin(g) + 0.0722 * sRGBtoLin(b); // per ITU-R BT.709

    if (luma <= 216 / 24389) {  // The CIE standard states 0.008856 but 216/24389 is the intent for 0.008856451679036
        return luma * (24389 / 27) > 50;  // The CIE standard states 903.3, but 24389/27 is the intent, making 903.296296296296296
    } else {
        return Math.pow(luma, 1 / 3) * 116 - 16 > 50;
    }
}

/**
 * Return black or white hexa color depending on the luminosity of the color.
 * @param color 
 * @param errorColor color to display if color is not a valid ColorHex, black by default
 * @returns black if the color is light, white otherwise
 * TODO unit test
 */
export function negativeBlackOrWhiteHex(color: string, errorColor = CSS_COLORS.black, options?: { disableWarn?: boolean }): ColorHex {
    let hexColor: ColorHex | undefined = color as ColorHex; // we do not use asColorHex because we don't want a console log if we have a rgb color
    if (!isValidHexColor(hexColor)) {
        // Before return error color, try to convert as rgb to hex
        hexColor = rgbToHex(color as ColorRgb);
        if (hexColor == null) {// If still not parsable color, return default error color
            if (!options?.disableWarn) {
                getLogConsole().warn(`Color '${color}' is not a valid hex color.`);
            }
            return asColorHex(errorColor);
        }
    }
    const expendedColor = expandShortHexColor(hexColor);
    return isLightHexColor(expendedColor) ? "#000000" : "#ffffff"; // TODO GMI - permettre d'utiliser des variables css
}

/** Will return the hexa value of css color name or value if hexa type */
export function nameToHexOrInherit(color: ColorNameOrHex): string {
    if (isValidHexColor(color) || CSS_COLOR_KEYWORDS.includes(color)) {
        return color;
    }
    return colorNameToHex(color) ?? "inherit";
}